/**
 * All current permissions from the `RBACPermission` table should be represented here
 * to streamline permission implementations. These permission types act as a way to
 * centralize the approved permissions and avoid having hanging or otherwise deprecated
 * permissions in any related implementations.
 */

import { OutputTemplatePermission } from "~/modules/output-templates/permissions";
import { PricingEnginePermission } from "~/modules/pricing-engine/permissions";
import { PlatformBillingPermission } from "~/utils/types/platform-billing-permission";

export { OutputTemplatePermission, PlatformBillingPermission, PricingEnginePermission };

export enum AccountBalancePermission {
  view = "accountBalances.view",
}

export enum AccountingPeriodPermission {
  close = "accountingPeriods.close",
  reopen = "accountingPeriods.reopen",
  view = "accountingPeriods.view",
  viewHistoric = "accountingPeriods.viewHistoric",
}

export enum AccountingFxPermission {
  manualRateOverride = "accounting.fx-manual-rate-override",
}

export enum BillPermission {
  create = "bills.create",
  delete = "bills.delete",
  update = "bills.update",
  view = "bills.view",
}

export enum InvoicePermission {
  create = "invoices.create",
  delete = "invoices.delete",
  update = "invoices.update",
  view = "invoices.view",
}

export enum AccountsPayableCreditNotePermission {
  create = "APCreditNotes.create",
  delete = "APCreditNotes.delete",
  update = "APCreditNotes.update",
  view = "APCreditNotes.view",
}

export enum AccountsPayablePaymentPermission {
  create = "APPayments.create",
  delete = "APPayments.delete",
  update = "APPayments.update",
  view = "APPayments.view",
}

export enum AccountsReceivableCreditNotePermission {
  create = "ARCreditNotes.create",
  delete = "ARCreditNotes.delete",
  update = "ARCreditNotes.update",
  view = "ARCreditNotes.view",
}

export enum AccountsReceiveablePaymentPermission {
  create = "ARPayments.create",
  delete = "ARPayments.delete",
  update = "ARPayments.update",
  view = "ARPayments.view",
}

export enum BankAccountPermission {
  create = "bankAccounts.create",
  delete = "bankAccounts.delete",
  edit = "bankAccounts.edit",
}

export enum BankTransactionPermission {
  create = "bankTransactions.create",
  delete = "bankTransactions.delete",
  edit = "bankTransactions.edit",
  view = "bankTransactions.view",
  payeesView = "bankTransactions.payees.view",
}

export enum CustomerPermission {
  create = "customer.create",
  delete = "customer.delete",
  update = "customer.update",
  view = "customer.view",
  viewSalesPerson = "customer.view:salesperson",
}

export enum CostsPermission {
  view = "costs.view",
}

export enum LedgerAccountPermission {
  create = "ledgerAccounts.create",
  delete = "ledgerAccounts.delete",
  edit = "ledgerAccounts.edit",
  view = "ledgerAccounts.view",
}

export enum LedgerTransactionPermission {
  create = "ledgerTransactions.create",
  delete = "ledgerTransactions.delete",
  update = "ledgerTransactions.update",
  view = "ledgerTransactions.view",
  templatesCreate = "ledgerTransactions.templates.create",
  templatesDelete = "ledgerTransactions.templates.delete",
  templatesEdit = "ledgerTransactions.templates.edit",
  templatesView = "ledgerTransactions.templates.view",
}

export enum NotePermission {
  create = "note.create",
  delete = "note.delete",
  update = "note.update",
  view = "note.view",
  viewSalesPerson = "note.view:salesperson",
  /** Tenant-admin incident surface (SC-2722). Lists owner-only auto notes. Not granted to Core/Finance/Sales. */
  adminManage = "note.admin:manage",
}

export enum OrderPermission {
  create = "order.create",
  delete = "order.delete",
  update = "order.update",
  view = "order.view",
  viewAssigned = "order.view:assigned",
  viewSalesPerson = "order.view:salesperson",
}

export enum LotPermission {
  create = "lots.create",
  delete = "lots.delete",
  update = "lots.update",
  view = "lots.view",
  amendCosts = "lots.amend-costs",
}

export enum ProductPermission {
  create = "products.create",
  delete = "products.delete",
  update = "products.update",
  view = "products.view",
  viewAll = "products.viewAll",
}

export enum PriceBookPermission {
  manage = "priceBook.manage",
}

export enum PurchaseOrderPermission {
  create = "purchase-orders.create",
  delete = "purchase-orders.delete",
  update = "purchase-orders.update",
  view = "purchase-orders.view",
}

export enum SupplierPermission {
  create = "supplier.create",
  delete = "supplier.delete",
  update = "supplier.update",
  view = "supplier.view",
}

export enum WorkOrderPermission {
  create = "work-orders.create",
  delete = "work-orders.delete",
  update = "work-orders.update",
  view = "work-orders.view",
}

export enum ReconciliationPermission {
  create = "reconciliations.create",
  delete = "reconciliations.delete",
  update = "reconciliations.update",
  view = "reconciliations.view",
  reportCreate = "reconciliations.report.create",
}

export enum ReportPermission {
  balanceSheetCreate = "reports.balanceSheet.create",
  customReportsGenerate = "reports.customReports.generate",
  incomeStatementCreate = "reports.incomeStatement.create",
  salesPerformanceView = "reports.salesPerformance.view",
  salesPerformanceViewSalesPerson = "reports.salesPerformance.view:salesperson",
  transactionsCreate = "reports.transactions.create",
  trialBalanceCreate = "reports.trialBalance.create",
}

export enum SystemPermission {
  viewSettings = "system.view:settings",
  companyPresenceView = "system:companyPresence:view",
  crossOrgRoutingManage = "system:crossOrgRouting:manage",
  tagsCreate = "system:tags:create",
  tagsDelete = "system:tags:delete",
  tagsUpdate = "system:tags:update",
  tagsView = "system:tags:view",
  lotStatesCreate = "system:lotStates:create",
  lotStatesDelete = "system:lotStates:delete",
  lotStatesUpdate = "system:lotStates:update",
  lotStatesView = "system:lotStates:view",
  warehouseLocationsCreate = "system:warehouseLocations:create",
  warehouseLocationsDelete = "system:warehouseLocations:delete",
  warehouseLocationsUpdate = "system:warehouseLocations:update",
  warehouseLocationsView = "system:warehouseLocations:view",
  automationsCreate = "system:automations:create",
  automationsDelete = "system:automations:delete",
  automationsUpdate = "system:automations:update",
}

export enum DocumentsSettingsPermission {
  update = "documents.settings.update",
}

export enum TermsOfPaymentPermission {
  create = "termsOfPayment.create",
  delete = "termsOfPayment.delete",
  update = "termsOfPayment.update",
  view = "termsOfPayment.view",
}

export enum UserPermission {
  delete = "users.delete",
  update = "users.update",
  view = "users.view",
}

export enum UserInvitationPermission {
  create = "users.invite.create",
  revoke = "users.invite.revoke",
  view = "users.invite.view",
}

export enum UserPermissionPermission {
  delete = "users.permissions.delete",
  edit = "users.permissions.edit",
}

export type RBACPermission =
  | `${AccountBalancePermission}`
  | `${AccountingFxPermission}`
  | `${AccountingPeriodPermission}`
  | `${AccountsPayableCreditNotePermission}`
  | `${AccountsPayablePaymentPermission}`
  | `${AccountsReceivableCreditNotePermission}`
  | `${AccountsReceiveablePaymentPermission}`
  | `${BankAccountPermission}`
  | `${BankTransactionPermission}`
  | `${BillPermission}`
  | `${CostsPermission}`
  | `${CustomerPermission}`
  | `${InvoicePermission}`
  | `${LedgerAccountPermission}`
  | `${LedgerTransactionPermission}`
  | `${NotePermission}`
  | `${OrderPermission}`
  | `${OutputTemplatePermission}`
  | `${PlatformBillingPermission}`
  | `${PriceBookPermission}`
  | `${PricingEnginePermission}`
  | `${ReconciliationPermission}`
  | `${ReportPermission}`
  | `${SystemPermission}`
  | `${DocumentsSettingsPermission}`
  | `${TermsOfPaymentPermission}`
  | `${UserInvitationPermission}`
  | `${UserPermission}`
  | `${UserPermissionPermission}`
  | `${LotPermission}`
  | `${ProductPermission}`
  | `${PurchaseOrderPermission}`
  | `${SupplierPermission}`
  | `${WorkOrderPermission}`;

export const allRbacPermissions: string[] = [
  ...Object.values(AccountBalancePermission),
  ...Object.values(AccountingFxPermission),
  ...Object.values(AccountingPeriodPermission),
  ...Object.values(AccountsPayableCreditNotePermission),
  ...Object.values(AccountsPayablePaymentPermission),
  ...Object.values(AccountsReceivableCreditNotePermission),
  ...Object.values(AccountsReceiveablePaymentPermission),
  ...Object.values(BankAccountPermission),
  ...Object.values(BankTransactionPermission),
  ...Object.values(BillPermission),
  ...Object.values(CostsPermission),
  ...Object.values(CustomerPermission),
  ...Object.values(InvoicePermission),
  ...Object.values(LedgerAccountPermission),
  ...Object.values(LedgerTransactionPermission),
  ...Object.values(NotePermission),
  ...Object.values(OrderPermission),
  ...Object.values(OutputTemplatePermission),
  ...Object.values(PlatformBillingPermission),
  ...Object.values(PriceBookPermission),
  ...Object.values(PricingEnginePermission),
  ...Object.values(ReconciliationPermission),
  ...Object.values(ReportPermission),
  ...Object.values(SystemPermission),
  ...Object.values(DocumentsSettingsPermission),
  ...Object.values(TermsOfPaymentPermission),
  ...Object.values(UserInvitationPermission),
  ...Object.values(UserPermission),
  ...Object.values(UserPermissionPermission),
  ...Object.values(LotPermission),
  ...Object.values(ProductPermission),
  ...Object.values(PurchaseOrderPermission),
  ...Object.values(SupplierPermission),
  ...Object.values(WorkOrderPermission),
];
